import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GIVING_URL = 'https://giving.myamplify.io//app/giving/clodo-reachingpeoplefarfromgod';

const MajorGifts = ({ isStandalonePage = false }) => {
  const [activeTab, setActiveTab] = useState('give-now');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showGivingModal, setShowGivingModal] = useState(false);
  const [givingAmount, setGivingAmount] = useState('');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const buildGivingUrl = (amount) => {
    if (!amount) return GIVING_URL;
    // Pass amount via common query params — MyAmplify typically reads ?amount=
    return `${GIVING_URL}?amount=${encodeURIComponent(amount)}&gift_amount=${encodeURIComponent(amount)}`;
  };

  const openGivingModal = (amount = '') => {
    setGivingAmount(amount ? String(amount) : '');
    setShowGivingModal(true);
  };
  const closeGivingModal = () => setShowGivingModal(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (showGivingModal) {
      document.body.style.overflow = 'hidden';
      const onKey = (e) => {
        if (e.key === 'Escape') closeGivingModal();
      };
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', onKey);
      };
    }
  }, [showGivingModal]);

  const fetchSettings = async () => {
    try {
      const majorGiftsRes = await axios.get(`${BACKEND_URL}/api/major-gifts-settings`);
      setSettings(majorGiftsRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className={`${isStandalonePage ? 'py-16' : 'py-8'} bg-gradient-to-br from-kioo-primary to-kioo-secondary`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header (hide on standalone page since hero already shows it) */}
        {!isStandalonePage && (
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Major Gifts
            </h2>
            <p className="text-xl text-green-100 max-w-4xl mx-auto">
              Invest in sustainable, 24/7 gospel broadcasting across Liberia, Sierra Leone, and Guinea. Your leadership gift accelerates solar, studios, programming, and youth outreach.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column: Story & Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-white">

              {/* Investment Priorities (if standalone) */}
              {isStandalonePage && (
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold mb-4">Investment Priorities</h3>
                  <div className="space-y-3">
                    <div className="bg-white/10 rounded-lg p-4">
                      <h4 className="font-semibold">Solar & Power Resilience</h4>
                      <p className="text-sm text-green-100">Ensure 24/7 broadcasting capability</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4">
                      <h4 className="font-semibold">Cross-Border Programming</h4>
                      <p className="text-sm text-green-100">Reach Sierra Leone and Guinea</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4">
                      <h4 className="font-semibold">Youth & Community Outreach</h4>
                      <p className="text-sm text-green-100">Next generation engagement</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Actions */}
              <div className="space-y-4">
                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full bg-white text-kioo-primary py-3 px-6 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  💬 Talk to Us
                </button>

                {settings?.calUrl && (
                  <a
                    href={settings.calUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-kioo-secondary text-white py-3 px-6 rounded-lg font-semibold text-center hover:bg-opacity-90 transition-colors"
                  >
                    📅 Schedule a Call
                  </a>
                )}

                <a
                  href={`https://wa.me/231777981981?text=I'd like to discuss a major gift to Kioo Radio.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-green-500 text-white py-3 px-6 rounded-lg font-semibold text-center hover:bg-green-600 transition-colors"
                >
                  📱 WhatsApp Us
                </a>
              </div>
            </div>
          </div>

          {/* Right Column: Action Cards */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

              {/* Tab Headers */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('give-now')}
                  data-testid="mg-tab-give-now"
                  className={`flex-1 py-4 px-6 font-semibold transition-colors ${activeTab === 'give-now'
                    ? 'bg-kioo-primary text-white'
                    : 'text-gray-600 hover:text-kioo-primary'
                    }`}
                >
                  💳 Give Now
                </button>
                <button
                  onClick={() => setActiveTab('pledge')}
                  data-testid="mg-tab-check"
                  className={`flex-1 py-4 px-6 font-semibold transition-colors ${activeTab === 'pledge'
                    ? 'bg-kioo-primary text-white'
                    : 'text-gray-600 hover:text-kioo-primary'
                    }`}
                >
                  ✉️ Donate by Check
                </button>
                <button
                  onClick={() => setActiveTab('wire')}
                  data-testid="mg-tab-wire"
                  className={`flex-1 py-4 px-6 font-semibold transition-colors ${activeTab === 'wire'
                    ? 'bg-kioo-primary text-white'
                    : 'text-gray-600 hover:text-kioo-primary'
                    }`}
                >
                  🏦 Bank Transfer
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-6">

                {/* Give Now Tab */}
                {activeTab === 'give-now' && (
                  <div>
                    <h3 className="text-2xl font-semibold mb-4">Make a Leadership Gift</h3>

                    <p className="text-gray-600 mb-6">
                      Give securely online through our trusted giving platform. Your transaction is encrypted and secure.
                    </p>

                    {/* Amount Chips */}
                    <div className="flex flex-wrap gap-3 mb-6">
                      {['500', '1000', '5000', '10000'].map(amount => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => openGivingModal(amount)}
                          data-testid={`mg-amount-${amount}`}
                          className="px-4 py-2 border-2 border-kioo-primary text-kioo-primary rounded-lg hover:bg-kioo-primary hover:text-white transition-colors"
                        >
                          ${Number(amount).toLocaleString()}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => openGivingModal('')}
                        data-testid="mg-amount-other"
                        className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:border-kioo-primary hover:text-kioo-primary transition-colors"
                      >
                        Other Amount
                      </button>
                    </div>

                    {/* Liberia Vox Radio Note */}
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 mb-6">
                      <p className="text-sm text-emerald-800">
                        <strong>Important:</strong> Please select{' '}
                        <strong>"Liberia Vox Radio"</strong> on the giving page as your designation to direct your gift to Kioo Radio.
                      </p>
                    </div>

                    {/* Give Online Now Button */}
                    <button
                      type="button"
                      onClick={() => openGivingModal('')}
                      data-testid="mg-give-online-now-button"
                      className="w-full inline-flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-kioo-primary to-kioo-secondary text-white rounded-xl font-bold text-lg hover:opacity-95 transition-all shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      Give Online Now
                    </button>

                    <div className="flex items-center gap-4 text-xs text-gray-500 justify-center pt-4">
                      <span className="flex items-center gap-1">🔒 SSL Secure</span>
                      <span className="flex items-center gap-1">🛡️ Tax Deductible</span>
                      <span className="flex items-center gap-1">📧 Receipt Emailed</span>
                    </div>
                  </div>
                )}

                {/* Donate by Check Tab */}
                {activeTab === 'pledge' && (
                  <div>
                    <h3 className="text-2xl font-semibold mb-4">Donate by Check</h3>
                    <p className="text-gray-600 mb-6">
                      Prefer to give by mail? You can send a check directly to our ministry address below. Your gift is tax-deductible and a receipt will be mailed to you.
                    </p>

                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-4">
                      <p className="font-semibold text-gray-900 mb-3">Mail checks to:</p>
                      <address className="text-gray-700 not-italic text-lg leading-relaxed">
                        <strong>Reaching People Far From God (RPFFG)</strong><br />
                        P.O. Box 113<br />
                        Banning, CA 92220<br />
                        USA
                      </address>
                    </div>

                    <p className="text-sm text-gray-600">
                      Please write <strong>"Kioo Radio"</strong> in the memo line so your gift is properly designated.
                    </p>
                  </div>
                )}

                {/* Bank Transfer (ACH) Tab */}
                {activeTab === 'wire' && (
                  <div>
                    <h3 className="text-2xl font-semibold mb-4">Bank Transfer (ACH)</h3>
                    <p className="text-gray-700 text-lg leading-relaxed mb-6">
                      Support may also be given through a direct bank-to-bank transfer using ACH. This method is secure and reliable.
                    </p>

                    <div className="bg-blue-50 rounded-xl p-5 border border-blue-200 mb-6">
                      <p className="text-blue-900">
                        For ACH transfer details, please contact us directly and we'll provide you with the necessary banking information.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <a
                        href="mailto:admin@proudlyliberian.com?subject=ACH%20Bank%20Transfer%20Details%20Request"
                        data-testid="mg-ach-email-link"
                        className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl text-gray-700 hover:text-kioo-primary hover:shadow-md transition-all border border-gray-200"
                      >
                        <svg className="w-5 h-5 text-kioo-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="font-medium break-all">admin@proudlyliberian.com</span>
                      </a>
                      <a
                        href="tel:+17197268610"
                        data-testid="mg-ach-phone-link"
                        className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl text-gray-700 hover:text-kioo-primary hover:shadow-md transition-all border border-gray-200"
                      >
                        <svg className="w-5 h-5 text-kioo-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="font-medium">+1 719 726 8610</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Impact Documents (if standalone) */}
        {isStandalonePage && (settings?.caseForSupportUrl || settings?.impactBriefUrl || settings?.budgetOverviewUrl) && (
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-semibold text-white mb-8">Impact Documents</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {settings?.caseForSupportUrl && (
                <a
                  href={settings.caseForSupportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 backdrop-blur-sm text-white py-3 px-6 rounded-lg font-semibold hover:bg-white/20 transition-colors"
                >
                  📄 Case for Support
                </a>
              )}

              {settings?.impactBriefUrl && (
                <a
                  href={settings.impactBriefUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 backdrop-blur-sm text-white py-3 px-6 rounded-lg font-semibold hover:bg-white/20 transition-colors"
                >
                  📊 Latest Impact Brief
                </a>
              )}

              {settings?.budgetOverviewUrl && (
                <a
                  href={settings.budgetOverviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 backdrop-blur-sm text-white py-3 px-6 rounded-lg font-semibold hover:bg-white/20 transition-colors"
                >
                  💰 Budget Overview
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Contact About Major Gifts</h3>
              <button
                onClick={() => setShowContactModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
                <input
                  type="email"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                  placeholder="I'm interested in discussing a major gift to support Kioo Radio's mission..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowContactModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary"
                >
                  Send Message
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Giving Modal (MyAmplify iframe) */}
      {showGivingModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-6 bg-black/70 backdrop-blur-sm"
          onClick={closeGivingModal}
          data-testid="mg-giving-modal-overlay"
        >
          <div
            className="relative w-full max-w-5xl h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="mg-giving-modal"
          >
            <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-gradient-to-r from-kioo-primary to-kioo-secondary text-white flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div className="min-w-0">
                  <h3 className="font-bold text-base md:text-lg truncate">
                    Give to Kioo Radio{givingAmount ? ` — $${Number(givingAmount).toLocaleString()}` : ''}
                  </h3>
                  <p className="text-xs text-green-100 truncate">Select "Liberia Vox Radio" on the giving page</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={buildGivingUrl(givingAmount)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                  data-testid="mg-giving-modal-open-new-tab"
                >
                  Open in new tab
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={closeGivingModal}
                  data-testid="mg-giving-modal-close-button"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 relative bg-gray-50">
              <iframe
                src={buildGivingUrl(givingAmount)}
                title="Kioo Radio Online Giving"
                className="absolute inset-0 w-full h-full border-0"
                allow="payment *; clipboard-write"
                data-testid="mg-giving-modal-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MajorGifts;
